import React from 'react';

import { useIntl } from 'react-intl-next';

import BeachUmbrellaIcon from '@atlaskit/icon-lab/core/beach-umbrella';
import JiraIcon from '@atlaskit/icon-lab/core/jira';
import PremiumIcon from '@atlaskit/icon/core/premium';
import { ConfluenceLogo } from '@atlaskit/logo';
import { Modal } from '@atlassian/growth-pattern-library-modal';

import { ScreenEvent, useAnalytics } from '../../utils/analytics';
import {
	type CommonAnalyticsAttributes,
	CONFLUENCE_AFTER_STANDARD_UPGRADE_CALENDAR_MODAL,
	type ModalDismissalMethod,
	type ModalShownPreOrPostUpgrade,
	type OfferShownType,
	type SupportedProduct,
} from '../../utils/types';
import { useModalState } from '../../utils/useModalState';
import { StandardOfferCalendarModalRightSide } from '../ConfluenceStandardOfferCalendarModal';
import { EditionHighlightsLeftContent } from '../EditionHighlightsLeftContent';
import { PrimaryAndSecondaryCTAActionsGroup } from '../PrimaryAndSecondaryCTAActionsGroup';
import { SideContentWithModalCloseButton } from '../SideContentWithModalCloseButton';

import { messages } from './messages';

export const CALENDAR_URL_STRING = '/wiki/mycalendar';

export const ConfluenceAfterStandardUpgradeCalendarModal = ({
	calendarUrl,
}: {
	calendarUrl: string;
}) => {
	const intl = useIntl();

	const { isOpen, handleClose } = useModalState();

	const componentAttributes: CommonAnalyticsAttributes = {
		product: 'confluence' as SupportedProduct,
		offerShown: 'CALENDAR' as OfferShownType,
		modalShownPrePostUpgrade: 'POST_UPGRADE' as ModalShownPreOrPostUpgrade,
	};

	const { clickedDismissModal, clickedPrimaryCTAOnModal } = useAnalytics();

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			height="560px"
			logo={<ConfluenceLogo shouldUseNewLogoDesign size="xsmall" />}
			title={intl.formatMessage(messages.confluenceAfterUpgradeCalendarStandardModalTitle)}
			subtitle={intl.formatMessage(messages.confluenceAfterUpgradeCalendarStandardModalSubtitle)}
			testId="growth-pattern-library-modal"
			onClose={() => {
				clickedDismissModal(CONFLUENCE_AFTER_STANDARD_UPGRADE_CALENDAR_MODAL, {
					...componentAttributes,
					dismissalMethod: 'modalOverlayClick' as ModalDismissalMethod,
				});
				handleClose();
			}}
			sideContent={SideContentWithModalCloseButton(
				<StandardOfferCalendarModalRightSide />,
				() => {
					clickedDismissModal(CONFLUENCE_AFTER_STANDARD_UPGRADE_CALENDAR_MODAL, {
						...componentAttributes,
						dismissalMethod: 'closeButton' as ModalDismissalMethod,
					});
					handleClose();
				},
			)}
			actions={
				<PrimaryAndSecondaryCTAActionsGroup
					href={calendarUrl}
					onPrimaryCTAClick={() => {
						clickedPrimaryCTAOnModal(
							CONFLUENCE_AFTER_STANDARD_UPGRADE_CALENDAR_MODAL,
							componentAttributes,
						);
					}}
					onSecondaryCTAClick={() => {
						clickedDismissModal(CONFLUENCE_AFTER_STANDARD_UPGRADE_CALENDAR_MODAL, {
							...componentAttributes,
							dismissalMethod: 'secondaryCTAButton' as ModalDismissalMethod,
						});
						handleClose();
					}}
					primaryCTALabel={intl.formatMessage(
						messages.confluenceAfterUpgradeCalendarStandardModalPrimaryCTA,
					)}
					secondaryCTALabel={intl.formatMessage(
						messages.confluenceAfterUpgradeCalendarStandardModalSecondaryCTA,
					)}
				/>
			}
		>
			<EditionHighlightsLeftContent
				highlights={[
					{
						icon: BeachUmbrellaIcon,
						title: intl.formatMessage(
							messages.confluenceAfterUpgradeCalendarStandardModalEventTypes,
						),
					},
					{
						icon: JiraIcon,
						title: intl.formatMessage(
							messages.confluenceAfterUpgradeCalendarStandardModalJiraWorkItems,
						),
					},
					{
						icon: PremiumIcon,
						title: intl.formatMessage(
							messages.confluenceAfterUpgradeCalendarStandardModalLinkEventsToSpaces,
						),
					},
				]}
			/>
			<ScreenEvent name="upgradeFreePoolTargetExperimentModal" attributes={componentAttributes} />
		</Modal>
	);
};
